import { useEffect } from 'react';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import sql from 'highlight.js/lib/languages/sql';
import css from 'highlight.js/lib/languages/css';
import xml from 'highlight.js/lib/languages/xml'; // couvre html & xml
import java from 'highlight.js/lib/languages/java';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import yaml from 'highlight.js/lib/languages/yaml';
import csharp from 'highlight.js/lib/languages/csharp';
import cpp from 'highlight.js/lib/languages/cpp';
import php from 'highlight.js/lib/languages/php';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('css', css);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('java', java);
hljs.registerLanguage('python', python);
hljs.registerLanguage('json', json);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('shell', bash);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('csharp', csharp);
hljs.registerLanguage('cpp', cpp);
hljs.registerLanguage('php', php);

/**
 * Prétraite une chaîne HTML en appliquant la coloration syntaxique hljs
 * directement sur le texte HTML AVANT de le passer à dangerouslySetInnerHTML.
 * Les spans hljs-* sont ainsi présents dans le HTML initial et survivent
 * à toutes les re-renders React.
 */
export const highlightHtml = (html) => {
    if (!html || !html.includes('<pre')) return html;
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        doc.querySelectorAll('pre code').forEach((codeEl) => {
            const langClass = [...codeEl.classList].find(c => c.startsWith('language-'));
            const lang = langClass ? langClass.replace('language-', '') : null;
            try {
                const rawText = codeEl.textContent || '';
                if (lang && hljs.getLanguage(lang)) {
                    const result = hljs.highlight(rawText, { language: lang });
                    codeEl.innerHTML = result.value;
                } else if (!lang) {
                    const result = hljs.highlightAuto(rawText);
                    codeEl.innerHTML = result.value;
                }
            } catch (e) {
                // ignore
            }
        });
        return doc.body.innerHTML;
    } catch (e) {
        return html;
    }
};

/**
 * Injecte un header DOM (label langage + bouton copier) sur chaque <pre>
 * dans un container. La coloration est gérée par highlightHtml() en amont.
 */
const useCodeBlockEnhancer = (containerRef, deps = []) => {
    useEffect(() => {
        if (!containerRef?.current) return;

        const container = containerRef.current;

        const preElements = container.querySelectorAll('pre');

        const cleanups = [];

        const iconSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
        const checkSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

        preElements.forEach((pre) => {
            // Éviter de doubler les headers
            if (pre.querySelector('.code-block-header')) return;

            // Marquer le pre comme enrichi (cache le ::before CSS)
            pre.classList.add('code-enhanced');

            // Lire le langage depuis data-language ou la classe de <code>
            let lang = pre.getAttribute('data-language') || '';
            if (!lang) {
                const codeEl = pre.querySelector('code');
                if (codeEl) {
                    const langClass = [...codeEl.classList].find(c => c.startsWith('language-'));
                    if (langClass) lang = langClass.replace('language-', '');
                }
            }

            // Créer le header (langage + bouton copier)
            const header = document.createElement('div');
            header.className = 'code-block-header';

            const langLabel = document.createElement('span');
            langLabel.className = 'code-lang-label';
            langLabel.textContent = lang ? lang.toUpperCase() : 'CODE';

            const btn = document.createElement('button');
            btn.className = 'code-copy-btn';
            btn.type = 'button';
            btn.setAttribute('aria-label', 'Copier le code');
            btn.title = 'Copier';

            const setDefault = () => {
                btn.innerHTML = `${iconSVG}<span>Copier</span>`;
                btn.classList.remove('copied');
            };

            const setCopied = () => {
                btn.innerHTML = `${checkSVG}<span>Copié !</span>`;
                btn.classList.add('copied');
            };

            setDefault();

            let resetTimeout = null;

            const handleClick = (e) => {
                e.stopPropagation();
                const code = pre.querySelector('code');
                const text = code ? code.innerText : pre.innerText;
                navigator.clipboard.writeText(text).then(() => {
                    setCopied();
                    clearTimeout(resetTimeout);
                    resetTimeout = setTimeout(setDefault, 2000);
                }).catch(() => {
                    const ta = document.createElement('textarea');
                    ta.value = text;
                    ta.style.position = 'fixed';
                    ta.style.opacity = '0';
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                    setCopied();
                    clearTimeout(resetTimeout);
                    resetTimeout = setTimeout(setDefault, 2000);
                });
            };

            btn.addEventListener('click', handleClick);

            header.appendChild(langLabel);
            header.appendChild(btn);
            pre.insertBefore(header, pre.firstChild);

            cleanups.push(() => {
                clearTimeout(resetTimeout);
                btn.removeEventListener('click', handleClick);
                if (header.parentNode) header.remove();
                pre.classList.remove('code-enhanced');
            });
        });

        return () => cleanups.forEach((fn) => fn());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};

export default useCodeBlockEnhancer;
